import {
  EIremboFormlyFieldTypes,
  ICascadingConfig,
  LOCATION_COUNTRY_KEY,
  LOCATION_WIDGET_LEVELS,
  alphaNanoIdUtil,
} from '@irembo-andela/irembogov3-common';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { LOCATION_WIDGET_LOCAL_LABEL } from '../../config/location-widget-field-labels.config';

export const buildWidgetCountryField = (
  countryFieldKey: string,
  label: string,
  locationFirstLevel: string | undefined,
  fieldKey?: string
): FormlyFieldConfig => {
  const key = fieldKey ?? countryFieldKey;
  const countryIsFirstLevel = locationFirstLevel?.toLowerCase() === 'country';
  const countryField: FormlyFieldConfig = {
    type: EIremboFormlyFieldTypes.customdropdown,
    key,
    id: key,
    props: {
      label,
      dragDisabled: true,
      useBaseUrl: true,
      subType: 'CUSTOM_DROPDOWN_DATASET',
      multiple: false,
      dataset: {
        url: `/admin/v1/dataset-items/by-dataset-code/COUNTRY`,
        params: { size: 50 },
        dataField: 'data',
        pagination: 'true',
        searching: false,
      },
      bindLabel: 'label',
      bindValue: 'label',
      code: LOCATION_COUNTRY_KEY,
      datasetName: 'Country',
      placeholder: 'Enter country',
    },
    validation: {
      messages: {
        required: 'Field is required',
      },
    },
  };

  // Only attach a `hide` expression when it actually hides the field. This was
  // previously interpolated from the first-level check, which resolves at build
  // time to the constant string 'false' whenever Country IS the first level.
  // The builder canvas treats any field carrying a `hide` expression as
  // conditionally hidden, so a freshly added country dropdown was flagged as
  // conditional before the designer had written any condition.
  if (!countryIsFirstLevel) {
    countryField.expressions = { hide: 'true' };
  }

  return countryField;
};

export const buildWidgetCityField = (
  countryFieldKey: string,
  locationFirstLevel: string,
  enableInternationalCity: boolean,
  label: string,
  fieldKey?: string
): FormlyFieldConfig => {
  const cityFieldKey = fieldKey ?? `LocationCity${alphaNanoIdUtil(5)}`;
  const cityField: FormlyFieldConfig = {
    key: cityFieldKey,
    id: cityFieldKey,
    type: EIremboFormlyFieldTypes.input,
    props: {
      dragDisabled: true,
      label,
      disabled: false,
      required: false,
      placeholder: 'Enter city',
    },
    validation: {
      messages: {
        required: 'Field is required',
      },
    },
  };

  if (
    enableInternationalCity &&
    locationFirstLevel.toUpperCase() === LOCATION_COUNTRY_KEY
  ) {
    cityField.expressions = {
      hide: `model?.${countryFieldKey}?.toString()?.toLowerCase() === 'rwanda'`,
      'props.required': `model?.${countryFieldKey}?.toString()?.toLowerCase() !== 'rwanda'`,
    };
  } else {
    cityField.expressions = {
      hide: 'true',
    };
  }

  return cityField;
};

export const buildCascadeField = (
  countryFieldKey: string,
  locationFirstLevel: string,
  locationLastLevel: string,
  label: string = LOCATION_WIDGET_LOCAL_LABEL,
  required = false,
  nonConfigurable = false,
  fieldKey?: string
): FormlyFieldConfig => {
  const locationWidgetCascadeFieldKey =
    fieldKey ?? `LocalLocation${alphaNanoIdUtil(5)}`;
  const configs: ICascadingConfig[] = generateDatasetConfigs(
    locationFirstLevel,
    locationLastLevel,
    label,
    required
  );
  const cascadeField: FormlyFieldConfig = {
    type: EIremboFormlyFieldTypes.customcascadingdropdowns,
    key: locationWidgetCascadeFieldKey,
    id: locationWidgetCascadeFieldKey,
    props: {
      label,
      configs,
      required,
      nonConfigurable,
      dragDisabled: true,
    },
  };
  if (locationFirstLevel.toUpperCase() === LOCATION_COUNTRY_KEY) {
    return {
      ...cascadeField,
      expressions: {
        hide: `model?.${countryFieldKey}?.toString()?.toLowerCase() !== 'rwanda'`,
      },
    };
  }
  return cascadeField;
};

export const generateDatasetConfigs = (
  firstLevel: string,
  lastLevel: string | undefined,
  labelPrefix: string,
  required: boolean
): ICascadingConfig[] => {
  const datasetOptions: string[] = Object.keys(LOCATION_WIDGET_LEVELS);
  const firstLevelIndex: number | undefined =
    datasetOptions.indexOf(firstLevel);

  const randomKey: string = alphaNanoIdUtil(5);

  const datasetConfigs: ICascadingConfig[] = [];
  const datasetFieldKeys: Record<string, string> = {};

  datasetOptions.every((option: string, i: number) => {
    datasetFieldKeys[option] = `${option}`;
    if (i < firstLevelIndex) {
      return true;
    }

    if (
      option.toLowerCase() === firstLevel?.toLowerCase() ||
      option.toLowerCase() ===
        Object.keys(LOCATION_WIDGET_LEVELS)[0].toLowerCase()
    ) {
      datasetConfigs.push(
        createDataset(
          `${datasetFieldKeys[option]}${randomKey}`,
          `${labelPrefix} ${LOCATION_WIDGET_LEVELS[option]}`,
          option,
          required
        )
      );
      return true;
    }

    datasetConfigs.push(
      createDataset(
        `${datasetFieldKeys[option]}${randomKey}`,
        `${labelPrefix} ${LOCATION_WIDGET_LEVELS[option]}`,
        null,
        required,
        `${datasetFieldKeys[datasetOptions[i - 1]]}${randomKey}` ?? null
      )
    );

    if (option.toLowerCase() === lastLevel?.toLowerCase()) {
      return false;
    }
    return true;
  });

  return datasetConfigs;
};

export const createDataset = (
  key: string,
  label: string,
  datasetCode: string | null,
  required: boolean,
  parent: string | null = null
): ICascadingConfig => {
  const url = datasetCode
    ? `/admin/v1/dataset-items/by-dataset-code/${datasetCode}`
    : '/admin/v1/dataset-items/by-details';
  const dataset: ICascadingConfig = {
    key,
    label,
    parent,
    dataset: {
      url,
      dataField: 'data',
      useBaseUrl: true,
    },
    required,
    bindlabel: 'label',
    bindvalue: '',
    placeholder: `Select ${label}`,
  };

  if (!datasetCode) {
    dataset['parentBindKey'] = 'id';
  }

  return dataset;
};
